import { useCallback } from 'react';
import { Button, Stack, Text } from '@adc/parallax-component-library';
import { Dialog } from '@tamagui/dialog';

import { ModalContent } from '~/components/ModalContent';
import { ModalOverlay } from '~/components/ModalOverlay';
import { useModal } from '~/features/modals/useModal';
import { buildTestId } from '~/utils/buildTestId';

type ArchiveDepartmentModalProps = {
  testID: string;
  departmentName: string;
  onArchive: () => Promise<void>;
};

export const ArchiveDepartmentModal = ({
  testID,
  departmentName,
  onArchive,
}: ArchiveDepartmentModalProps) => {
  const { closeModal } = useModal();

  const onClose = useCallback(() => {
    closeModal('ArchiveDepartment');
  }, [closeModal]);

  const onOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        onClose();
      }
    },
    [onClose],
  );

  const handleDepartmentArchive = useCallback(async () => {
    await onArchive();
    onClose();
  }, [onClose, onArchive]);

  return (
    <Dialog open onOpenChange={onOpenChange} modal>
      <Dialog.Portal testID={buildTestId({ testID, suffix: 'portal' })}>
        <ModalOverlay testID={buildTestId({ testID, suffix: 'overlay' })} />

        <ModalContent
          testID={buildTestId({
            testID,
            suffix: 'content',
          })}
          key="content"
          maxWidth={800}
          gap="$6"
        >
          <Text type="$h3.medium">Confirm Archive Department</Text>
          <Text type="$bodyBase.medium">
            Are you sure you want to archive {departmentName}?
            <br />
            Users assigned to this department will lose access to this
            department.
          </Text>
          <Stack
            testID={buildTestId({ testID, suffix: 'actions' })}
            flexDirection="row"
            justifyContent="flex-end"
            gap="$4"
          >
            <Button
              label="Cancel"
              aria-label="Cancel Department Archivation"
              variant="secondary"
              testID={buildTestId({
                testID,
                suffix: ['cancel', 'button'],
              })}
              onPress={onClose}
            />
            <Button
              label="Archive"
              aria-label="Archive Department"
              variant="primary"
              testID={buildTestId({
                testID,
                suffix: ['archive', 'button'],
              })}
              onPress={() => void handleDepartmentArchive()}
            />
          </Stack>
        </ModalContent>
      </Dialog.Portal>
    </Dialog>
  );
};
